import { reasonCopyKey } from 'components/Intelligence/reasonKey';
import i18n from 'utils/i18n';

import { TCustomerInsights } from '../i18n/TModules';
import { CardConfidence } from '../types';
import { confidenceBandFor, MetricConfidence } from './confidence';

/**
 * INTERIM FRONTEND COMPUTATION — DELETE THIS MODULE WHEN THE WIRE CARRIES A CARD-LEVEL SCORE.
 *
 * THE one card-confidence computation, shared by both layers that draw a pill: Customer Insights
 * (`O-*`, PLA-8815) and Customer 360 (`C-*`, PLA-8828). Both derive from the same catalogue sheet,
 * so they must not be two implementations free to drift into disagreeing about a customer's
 * confidence. What genuinely differs between them is WHICH metrics are eligible, and that is the
 * whole of the per-layer part: each layer passes its own `Show Confidence Score?` table in as
 * `displayRuleFor` (`registry/customer360Confidence.ts`, `registry/customerInsightsConfidence.ts`).
 * An identifier the given table does not carry is not eligible -- which is also what stops a card
 * key BOTH registries declare from reaching the other layer's catalogue.
 *
 * The Metrics Catalogue assigns this calculation to the backend. `Calculation & Governance Rules`
 * states the ownership line as *"Backend calculates; FE renders"*, and G32 of the same sheet is
 * explicit that the FE *"must render the server-provided card score/band and must not independently
 * recalculate it"*. Everything below therefore VIOLATES G32 knowingly and temporarily: the backend
 * has not shipped card-level confidence, and the decision (PLA-8828, this session) was to compute it
 * here in the meantime so a scored card is not drawn permanently unscored. Both layers gate the
 * call behind `assembleCard`'s `interimCardConfidence` opt-in, so nothing is drawn by default.
 *
 * Consequences that follow from that, and which is why the whole computation is in ONE file:
 *
 *   - The moment a card payload carries its own score and band, `assembleCard` stops calling this
 *     and the file is deleted whole, both layer binders with it. Nothing else in the tree
 *     implements a rule from this sheet.
 *   - Nothing here may be reused as a general scoring model. It aggregates scores the SERVER
 *     published per metric; it never derives a metric score from confidence components.
 *
 * The rules implemented, verbatim in substance from the catalogue:
 *
 *   1. Eligibility  — only metrics whose `Show Confidence Score? = Yes` are included; `No`,
 *      `Metric Itself`, hidden and Not Applicable metrics are excluded. *"If a card has no
 *      applicable Yes metrics, do not calculate a standard Card Confidence solely from
 *      No/Metric Itself metrics."*
 *   2. Raw score   — *"Card Confidence Raw = arithmetic mean of metric-level Result Confidence for
 *      applicable/displayed metrics on that card where Show Confidence Score? = Yes."* Equal
 *      weights, per *"equal weights if no approved weights"*; no approved weights exist.
 *   3. Critical cap — *"If any primary metric included in the card carries an active governed
 *      Critical confidence reason with Confidence Cap = 0.59, Final Card Confidence =
 *      MIN(Card Confidence Raw, 0.59)."*
 *   4. Bands       — High >=0.80, Medium 0.60-0.79, Low 0.40-0.59, Insufficient <0.40, read from
 *      `data/confidence.ts` rather than restated here.
 *   5. Leading reason — *"Use Precedence: Highest -> High -> Normal. If multiple active
 *      Limiting/Critical reasons share the same precedence, select the reason tied to the lowest
 *      confidence-component score; if still tied, use stable component order: Input Coverage ->
 *      Source Reliability -> Data Freshness -> Identity / Entity Match -> Calculation / Model
 *      Reliability. Positive reasons surface only when no Limiting/Critical reason is active."*
 *      Plus the card rule's own tie-break: *"stable metric ID tie-break."*
 */

/**
 * The `Confidence Reason Catalogue` sheet, as the fields the two governed decisions need: which
 * reasons CAP a score, and how a leading reason is chosen between several.
 *
 * The cap is read off `cap` and never off a list of reason codes, which is what the catalogue asks
 * for -- *"use the existing Critical reason catalogue/cap rather than hard-coding only named reason
 * codes"*. Three reasons carry it today and a fourth added to the sheet must cap without a code
 * change here.
 *
 * `componentOrder` is the sheet's own stable component order, 0-4: Input Coverage, Source
 * Reliability, Data Freshness, Identity / Entity Match, Calculation / Model Reliability.
 *
 * Display copy is deliberately NOT here: all 36 codes are translated under
 * `ConfidenceGovernance.Reasons`, keyed by the PascalCase `reasonCopyKey` derives, and a second copy
 * of the sentence in a constant would be the one that goes stale.
 */
export interface ConfidenceReasonRule {
  componentOrder: number;
  impact: 'Critical' | 'Limiting' | 'Positive';
  precedence: 'Highest' | 'High' | 'Normal';
  /** The governed Confidence Cap, or `null` where the reason caps nothing. */
  cap: number | null;
}

export const CONFIDENCE_REASON_RULES: Record<string, ConfidenceReasonRule> = {
  // Input Coverage
  INPUT_COVERAGE_COMPLETE: {
    componentOrder: 0,
    impact: 'Positive',
    precedence: 'Normal',
    cap: null
  },
  INPUT_COVERAGE_HIGH: { componentOrder: 0, impact: 'Positive', precedence: 'Normal', cap: null },
  INPUT_COVERAGE_PARTIAL: { componentOrder: 0, impact: 'Limiting', precedence: 'High', cap: null },
  INPUT_COVERAGE_LOW: { componentOrder: 0, impact: 'Limiting', precedence: 'High', cap: null },
  CRITICAL_INPUT_MISSING: {
    componentOrder: 0,
    impact: 'Critical',
    precedence: 'Highest',
    cap: 0.59
  },

  // Source Reliability
  SOURCE_VALIDATED: { componentOrder: 1, impact: 'Positive', precedence: 'Normal', cap: null },
  SOURCE_RELIABILITY_HIGH: {
    componentOrder: 1,
    impact: 'Positive',
    precedence: 'Normal',
    cap: null
  },
  SOURCE_RELIABILITY_LOW: { componentOrder: 1, impact: 'Limiting', precedence: 'High', cap: null },
  SOURCE_CONFLICT: { componentOrder: 1, impact: 'Limiting', precedence: 'High', cap: null },
  SOURCE_UNAVAILABLE: { componentOrder: 1, impact: 'Limiting', precedence: 'High', cap: null },
  CRITICAL_SOURCE_UNAVAILABLE: {
    componentOrder: 1,
    impact: 'Critical',
    precedence: 'Highest',
    cap: 0.59
  },

  // Data Freshness
  DATA_NEAR_REAL_TIME: { componentOrder: 2, impact: 'Positive', precedence: 'Normal', cap: null },
  DATA_FRESH_WITHIN_SLA: { componentOrder: 2, impact: 'Positive', precedence: 'Normal', cap: null },
  DATA_PARTIALLY_DELAYED: { componentOrder: 2, impact: 'Limiting', precedence: 'High', cap: null },
  DATA_STALE: { componentOrder: 2, impact: 'Limiting', precedence: 'High', cap: null },
  CRITICAL_SOURCE_STALE: {
    componentOrder: 2,
    impact: 'Critical',
    precedence: 'Highest',
    cap: 0.59
  },
  SOURCE_REFRESH_FAILED: { componentOrder: 2, impact: 'Limiting', precedence: 'High', cap: null },
  FRESHNESS_UNKNOWN: { componentOrder: 2, impact: 'Limiting', precedence: 'High', cap: null },

  // Identity / Entity Match
  IDENTITY_MATCH_STRONG: { componentOrder: 3, impact: 'Positive', precedence: 'Normal', cap: null },
  IDENTITY_MATCH_PARTIAL: { componentOrder: 3, impact: 'Limiting', precedence: 'High', cap: null },
  IDENTITY_CONFLICT: { componentOrder: 3, impact: 'Limiting', precedence: 'High', cap: null },
  ENTITY_MATCH_STRONG: { componentOrder: 3, impact: 'Positive', precedence: 'Normal', cap: null },
  ENTITY_MATCH_PARTIAL: { componentOrder: 3, impact: 'Limiting', precedence: 'High', cap: null },
  ENTITY_UNMATCHED: { componentOrder: 3, impact: 'Limiting', precedence: 'High', cap: null },

  // Calculation / Model Reliability
  CALCULATION_VALIDATED: { componentOrder: 4, impact: 'Positive', precedence: 'Normal', cap: null },
  CALCULATION_RELIABILITY_LOW: {
    componentOrder: 4,
    impact: 'Limiting',
    precedence: 'High',
    cap: null
  },
  RULE_VALIDATED: { componentOrder: 4, impact: 'Positive', precedence: 'Normal', cap: null },
  RULE_RELIABILITY_LOW: { componentOrder: 4, impact: 'Limiting', precedence: 'High', cap: null },
  MODEL_VALIDATED: { componentOrder: 4, impact: 'Positive', precedence: 'Normal', cap: null },
  BASELINE_RELIABLE: { componentOrder: 4, impact: 'Positive', precedence: 'Normal', cap: null },
  BASELINE_INSUFFICIENT: { componentOrder: 4, impact: 'Limiting', precedence: 'High', cap: null },
  BASELINE_UNSTABLE: { componentOrder: 4, impact: 'Limiting', precedence: 'High', cap: null },
  HISTORY_INSUFFICIENT: { componentOrder: 4, impact: 'Limiting', precedence: 'High', cap: null },
  MODEL_RELIABILITY_LOW: { componentOrder: 4, impact: 'Limiting', precedence: 'High', cap: null },
  PROXY_INPUT_USED: { componentOrder: 4, impact: 'Limiting', precedence: 'High', cap: null },
  SAMPLE_SIZE_LOW: { componentOrder: 4, impact: 'Limiting', precedence: 'High', cap: null }
};

/** The rule for a governed reason code, or `undefined` for a code the catalogue does not carry. */
export const confidenceReasonRuleFor = (
  code: string | null | undefined
): ConfidenceReasonRule | undefined => {
  const trimmed = typeof code === 'string' ? code.trim().toUpperCase() : '';

  return Object.prototype.hasOwnProperty.call(CONFIDENCE_REASON_RULES, trimmed)
    ? CONFIDENCE_REASON_RULES[trimmed]
    : undefined;
};

const PRECEDENCE_ORDER: Record<ConfidenceReasonRule['precedence'], number> = {
  Highest: 0,
  High: 1,
  Normal: 2
};

/**
 * The one code a list of governed reason codes governs with, or `null` where it carries none the
 * catalogue knows.
 *
 * ADDITIVE for PLA-9569, and read by nothing on the two card layers: `confidence_limiting_reason` is
 * ONE code per metric there, while Orbit's `metric_results[].confidence.reason_codes` (PLA-8844
 * contract section 6) is a list. Both governed decisions below -- the Critical cap and the leading
 * reason -- are stated about a single code, so a list is reduced to the one the sheet itself would
 * rank first: Limiting/Critical ahead of Positive, then Highest -> High -> Normal, then the stable
 * component order. Taking the list's first entry instead would let a Critical reason sitting second
 * lose its cap.
 */
export const governingReasonCode = (codes: readonly string[] | null | undefined): string | null => {
  const ranked = (Array.isArray(codes) ? codes : [])
    .map(code => ({
      code: typeof code === 'string' ? code.trim() : '',
      rule: confidenceReasonRuleFor(code)
    }))
    .filter(
      (entry): entry is { code: string; rule: ConfidenceReasonRule } =>
        entry.rule !== undefined && entry.code !== ''
    );

  // "Positive reasons surface only when no Limiting/Critical reason is active."
  const limiting = ranked.filter(entry => entry.rule.impact !== 'Positive');
  const candidates = limiting.length ? limiting : ranked;

  if (!candidates.length) {
    return null;
  }

  const [leading] = [...candidates].sort(
    (left, right) =>
      PRECEDENCE_ORDER[left.rule.precedence] - PRECEDENCE_ORDER[right.rule.precedence] ||
      left.rule.componentOrder - right.rule.componentOrder ||
      left.code.localeCompare(right.code)
  );

  return leading.code;
};

const REASONS_GROUP = `${TCustomerInsights}.ConfidenceGovernance.Reasons`;

/**
 * A layer's `Show Confidence Score?` table, as the only question this computation asks of it.
 *
 * Structural rather than an import of either registry's `ConfidenceDisplayRule`: the two declare the
 * identical union, and importing one of them here would make the shared rule a client of one layer.
 * `undefined` for an identifier the table does not carry -- which is what an identifier from the
 * OTHER layer is.
 */
export type ConfidenceDisplayRuleLookup = (
  identifier: string
) => 'yes' | 'no' | 'metricItself' | undefined;

/** One tile's contribution: the catalogue identifiers it reads, and the confidence they answered with. */
export interface CardConfidenceInput {
  /**
   * Every catalogue identifier this contribution names -- `identifiersNamedBy`, the same fan-out
   * `customer360CardShowsConfidence` / `customerInsightsCardShowsConfidence` gate the card on, so
   * the pill and the score cannot disagree about which metrics count. A `metric` tile names one; a
   * `ratio`/`difference` tile names its two operands.
   */
  identifiers: string[];
  confidence?: MetricConfidence;
}

interface EligibleMetric {
  identifier: string;
  confidence: MetricConfidence;
  score: number | null;
}

const isEligible = (identifiers: string[], displayRuleFor: ConfidenceDisplayRuleLookup): boolean =>
  identifiers.some(identifier => displayRuleFor(identifier) === 'yes');

const finiteScore = (confidence: MetricConfidence): number | null =>
  typeof confidence.score === 'number' && Number.isFinite(confidence.score)
    ? confidence.score
    : null;

/**
 * The catalogue's own Critical cap, used where `confidence_cap_applied` is the ONLY signal: the
 * boolean states that a cap applied without naming its value, and every Critical reason on the sheet
 * caps at this figure.
 */
const CRITICAL_CAP_FALLBACK = 0.59;

/**
 * The governed cap this reading imposes on its card, or `null` where it imposes none.
 *
 * Two signals, either sufficient: the reason catalogue's `cap` for the code in
 * `confidence_limiting_reason`, and the wire's own `confidence_cap_applied` boolean. Either alone is
 * enough because they state the same governed fact from two directions -- the sheet saying a cap
 * applies, and the backend having already applied one -- and a payload that sends only one of the
 * two must still cap the card.
 *
 * The value comes from the rule rather than a constant wherever a code names one, which is what the
 * catalogue asks for: *"use the existing Critical reason catalogue/cap rather than hard-coding only
 * named reason codes"*.
 */
const governedCap = (confidence: MetricConfidence): number | null => {
  const rule = confidenceReasonRuleFor(confidence.limitingReason);

  if (rule && rule.cap !== null) {
    return rule.cap;
  }

  return confidence.capApplied === true ? CRITICAL_CAP_FALLBACK : null;
};

/**
 * The one reason a card publishes, chosen by the sheet's precedence rule.
 *
 * DEVIATION, and it cannot be avoided: the sheet's second tie-break is *"the reason tied to the
 * lowest confidence-component score"*, but the wire carries no per-component scores at all -- only
 * the metric's overall `confidence_score`. That is used as the proxy. Inventing the five component
 * scores to rank against would be a fabrication, and the ordering it produced would not be the
 * sheet's either.
 *
 * Codes the reason catalogue does not carry are skipped rather than ranked last: an unknown code has
 * no stated impact, so it cannot be called limiting, and `reasonCopyKey` would refuse to render it
 * anyway.
 */
const leadingReasonCode = (eligible: EligibleMetric[]): string | null => {
  const ranked = eligible
    .map(metric => ({
      code:
        typeof metric.confidence.limitingReason === 'string'
          ? metric.confidence.limitingReason.trim()
          : '',
      rule: confidenceReasonRuleFor(metric.confidence.limitingReason),
      // A metric with no score sorts last within its precedence rather than first: "lowest score"
      // is a statement about a score that exists.
      score: metric.score === null ? Number.POSITIVE_INFINITY : metric.score,
      identifier: metric.identifier
    }))
    .filter(entry => entry.rule !== undefined && entry.code !== '');

  // "Positive reasons surface only when no Limiting/Critical reason is active."
  const limiting = ranked.filter(entry => entry.rule.impact !== 'Positive');
  const candidates = limiting.length ? limiting : ranked;

  if (!candidates.length) {
    return null;
  }

  const [leading] = [...candidates].sort(
    (left, right) =>
      PRECEDENCE_ORDER[left.rule.precedence] - PRECEDENCE_ORDER[right.rule.precedence] ||
      left.score - right.score ||
      left.rule.componentOrder - right.rule.componentOrder ||
      left.identifier.localeCompare(right.identifier)
  );

  return leading.code;
};

/**
 * A card's confidence from its own metrics' confidences, or `undefined` where the catalogue does not
 * let this card be scored.
 *
 * `undefined` -- never a zero -- in both of the cases the catalogue names: no eligible `Yes` metric
 * on the card at all, and no eligible metric that actually carried a `confidence_score`. A metric
 * with no confidence column is an absent measurement, not a measured zero, and averaging it in would
 * report a card as failing on the strength of a question that was never asked.
 *
 * The published `score` is a PERCENTAGE, because `CardConfidence` is what `ConfidenceBadge` renders
 * and its copy is `{{band}} · {{score}}%`. The band is taken from that same rounded figure rather
 * than from the unrounded mean, so the word and the number beside it can never contradict each other
 * on a boundary (a raw 0.795 reading `Medium confidence · 80%`).
 */
export const cardConfidenceFrom = (
  displayRuleFor: ConfidenceDisplayRuleLookup,
  inputs: CardConfidenceInput[]
): CardConfidence | undefined => {
  const eligible: EligibleMetric[] = (Array.isArray(inputs) ? inputs : [])
    .filter(input => input.confidence && isEligible(input.identifiers, displayRuleFor))
    .map(input => ({
      identifier: input.identifiers.find(id => displayRuleFor(id) === 'yes'),
      confidence: input.confidence,
      score: finiteScore(input.confidence)
    }))
    // The mean is over METRICS, not tiles: a card whose tiles read the same identifier twice would
    // otherwise weight that metric twice.
    .filter(
      (metric, index, all) =>
        all.findIndex(other => other.identifier === metric.identifier) === index
    );

  const scored = eligible.filter(metric => metric.score !== null);

  if (!scored.length) {
    return undefined;
  }

  const raw = scored.reduce((total, metric) => total + metric.score, 0) / scored.length;

  // Collected across every ELIGIBLE metric, not only the ones that carried a score: the rule caps on
  // a Critical reason being active on a metric included in the card, and a metric whose critical
  // input is missing badly enough to withhold its score is the strongest case for capping.
  const caps = eligible
    .map(metric => governedCap(metric.confidence))
    .filter((cap): cap is number => cap !== null);

  const final = caps.length ? Math.min(raw, Math.min(...caps)) : raw;

  const percentage = Math.round(final * 100);
  const code = leadingReasonCode(eligible);
  const copyKey = reasonCopyKey(REASONS_GROUP, code);

  return {
    score: percentage,
    band: confidenceBandFor(percentage / 100),
    // One reason, not three: the sheet asks for a single leading reason per card. An unresolvable
    // code publishes nothing rather than the identifier itself.
    reasons: copyKey ? [i18n.t(copyKey)] : []
  };
};

export default cardConfidenceFrom;
